import React, { useState, useCallback, useRef } from 'react';
import { Root, Portal, ItemText } from '@radix-ui/react-select';
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import {
  ValueContainer,
  SelectedValues,
  SelectedTag,
  Placeholder,
  SearchBarContainer,
  SearchIconWrapper,
  SearchInput,
  ItemContent,
  Checkmark,
  RemoveButton,
  SelectTrigger,
  SelectContent,
  SelectViewport,
  SelectItem,
} from './styles';
import { SelectRoot, TriggerContent } from '../RadixSelect/styles';

export interface MultiSelectOption {
  label: string;
  value: string;
}

export interface RadixMultiSelectProps {
  options: MultiSelectOption[];
  value?: MultiSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  onChange?: (selectedOptions: MultiSelectOption[]) => void;
  className?: string;
  'aria-label'?: string;
  closeMenuOnSelect?: boolean;
  portalContainer?: HTMLElement | null;
  customDisplayText?: string;
  chevronIcon?: React.ReactNode;
  calendarView?: boolean;
}
export const RadixMultiSelect: React.FC<RadixMultiSelectProps> = ({
  options,
  value = [],
  placeholder,
  disabled,
  onChange,
  className,
  'aria-label': ariaLabel,
  closeMenuOnSelect = false,
  portalContainer,
  customDisplayText,
  chevronIcon,
  calendarView = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const lastClickTime = useRef(0);
  const isRemovingItem = useRef(false);

  const handleOpenChange = useCallback((open: boolean) => {
    if (isRemovingItem.current && open) {
      return;
    }
    setIsOpen(open);
  }, []);

  const handleSelectItem = useCallback(
    (optionValue: string) => {
      const now = Date.now();
      if (now - lastClickTime.current < 100) {
        return;
      }
      lastClickTime.current = now;

      const option = options.find(
        (opt: MultiSelectOption) => opt.value === optionValue,
      );
      if (!option || !onChange) return;

      const isSelected = value.some(
        (v: MultiSelectOption) => v.value === optionValue,
      );

      let newValue: MultiSelectOption[];
      if (isSelected) {
        newValue = value.filter(
          (v: MultiSelectOption) => v.value !== optionValue,
        );
      } else {
        newValue = [...value, option];
      }

      onChange(newValue);

      if (closeMenuOnSelect) {
        setIsOpen(false);
      }
    },
    [options, value, onChange, closeMenuOnSelect],
  );

  const handleRemove = useCallback(
    (itemValue: string, e: React.MouseEvent) => {
      isRemovingItem.current = true;
      e.preventDefault();
      e.stopPropagation();
      handleSelectItem(itemValue);
      setTimeout(() => {
        isRemovingItem.current = false;
      }, 100);
    },
    [handleSelectItem],
  );

  const filteredOptions = search
    ? options.filter((option: MultiSelectOption) =>
        option.label.toLowerCase().includes(search.toLowerCase()),
      )
    : options;

  return (
    <SelectRoot>
      <Root open={isOpen} onOpenChange={handleOpenChange}>
        <SelectTrigger
          className={className}
          aria-label={ariaLabel}
          disabled={disabled}
          onPointerDown={(e) => {
            if (isRemovingItem.current) {
              e.preventDefault();
              e.stopPropagation();
              isRemovingItem.current = false;
            }
          }}
        >
          <TriggerContent>
            <ValueContainer>
              {value.length > 0 ? (
                <SelectedValues>
                  {customDisplayText ? (
                    <SelectedTag>{customDisplayText}</SelectedTag>
                  ) : (
                    value.map((item: MultiSelectOption) => (
                      <SelectedTag key={item.value}>
                        {item.label}
                        <RemoveButton
                          data-remove-button="true"
                          onMouseDown={(e) => handleRemove(item.value, e)}
                          onClick={(e) => handleRemove(item.value, e)}
                        >
                          ×
                        </RemoveButton>
                      </SelectedTag>
                    ))
                  )}
                </SelectedValues>
              ) : (
                <Placeholder>{placeholder}</Placeholder>
              )}
            </ValueContainer>
            {chevronIcon && (
              <span
                style={{
                  marginLeft: 8,
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--way-colors-contrastColorShades-60)',
                }}
              >
                {chevronIcon}
              </span>
            )}
          </TriggerContent>
        </SelectTrigger>

        <Portal container={portalContainer || undefined}>
          <SelectContent
            side="bottom"
            align="start"
            sideOffset={2}
            avoidCollisions={false}
            position="popper"
          >
            {/* Search bar at the top of the dropdown */}
            {calendarView && (
              <SearchBarContainer>
                <SearchIconWrapper>
                  <MagnifyingGlassIcon />
                </SearchIconWrapper>
                <SearchInput
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </SearchBarContainer>
            )}
            <SelectViewport>
              {filteredOptions.length === 0 ? (
                <div style={{ padding: '12px', color: '#888', fontSize: 14 }}>
                  No options
                </div>
              ) : (
                filteredOptions.map((option: MultiSelectOption, i) => {
                  const isSelected = value.some(
                    (v: MultiSelectOption) => v.value === option.value,
                  );
                  return (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      id={`react-select-2-option-${i}`}
                      onSelect={(e) => {
                        e.preventDefault();
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectItem(option.value);
                      }}
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        handleSelectItem(option.value);
                      }}
                      data-selected={isSelected}
                    >
                      <ItemContent>
                        {calendarView && isSelected && <Checkmark>✓</Checkmark>}
                        <ItemText>{option.label}</ItemText>
                      </ItemContent>
                    </SelectItem>
                  );
                })
              )}
            </SelectViewport>
          </SelectContent>
        </Portal>
      </Root>
    </SelectRoot>
  );
};
